import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Crown, Calendar, Receipt, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function fmtDate(v) {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function Account() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  const load = async () => {
    try {
      const res = await api.get("/account/subscription");
      setData(res.data);
    } catch {
    } finally {
      setLoadingData(false);
    }
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  const cancel = async () => {
    if (!window.confirm("Cancel your subscription? You'll keep access until the end of the current billing period.")) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/account/subscription/cancel");
      toast.success(r.message || "Subscription cancelled");
      await load();
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  if (loadingData || !data) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase animate-pulse" data-testid="account-loading">
          Loading your membership…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="flex items-center justify-between px-6 md:px-12 h-20 border-b border-white/10">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="IMN" className="h-8 w-auto object-contain" />
          <span className="font-display text-xl font-bold uppercase tracking-widest">Account</span>
        </div>
        <button
          onClick={() => navigate("/")}
          data-testid="account-back-btn"
          className="flex items-center gap-2 font-mono-imn text-xs uppercase tracking-widest text-white/60 hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Site
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="font-mono-imn text-xs tracking-[0.3em] text-gold uppercase mb-2">Your Membership</p>
          <h1 className="font-display text-4xl md:text-5xl font-bold uppercase mb-1">{user.name || "Member"}</h1>
          <p className="text-white/40 text-sm mb-10">{user.email}</p>

          {/* Status card */}
          <div className={`border p-8 mb-6 ${data?.is_premium ? "border-gold bg-white/[0.03]" : "border-white/10 bg-[#0A0A0A]"}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className={`p-3 border ${data?.is_premium ? "border-gold text-gold" : "border-white/20 text-white/40"}`}>
                  <Crown size={22} />
                </div>
                <div>
                  <p className="font-display text-2xl font-bold uppercase" data-testid="account-status">
                    {data?.is_premium ? "Premium Member" : "Free Account"}
                  </p>
                  <p className="text-white/50 text-sm">
                    {data?.is_premium
                      ? `${data.plan === "yearly" ? "Yearly" : "Monthly"} plan`
                      : "Upgrade to unlock premium originals"}
                  </p>
                </div>
              </div>
              {!data?.is_premium ? (
                <button
                  onClick={() => navigate("/#pricing")}
                  data-testid="account-upgrade-btn"
                  className="bg-white text-black font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:bg-white/90 active:scale-95 transition-all"
                >
                  Go Premium
                </button>
              ) : data?.cancel_at_period_end ? (
                <span className="border border-white/20 text-white/60 text-xs uppercase tracking-widest px-4 py-2">
                  Cancels at period end
                </span>
              ) : (
                <button
                  onClick={cancel}
                  disabled={busy}
                  data-testid="account-cancel-btn"
                  className="border border-white/25 px-6 py-3 text-sm uppercase tracking-wider hover:bg-red-500/20 hover:border-red-500/40 transition-colors disabled:opacity-50"
                >
                  {busy ? "Cancelling…" : "Cancel Plan"}
                </button>
              )}
            </div>
            {data?.is_premium && (
              <div className="flex items-center gap-2 mt-6 pt-6 border-t border-white/10 text-sm text-white/50">
                <Calendar size={15} className="text-gold" />
                {data?.cancel_at_period_end ? "Access ends" : "Renews / valid until"}:{" "}
                <span className="text-white">{fmtDate(data.premium_until)}</span>
              </div>
            )}
          </div>

          {/* Live Pass status (only when Local Cable is running or user already holds a pass) */}
          {(data?.live_enabled || data?.has_live_pass) && (
          <div className={`border p-6 mb-6 flex items-center justify-between flex-wrap gap-4 ${data?.has_live_pass ? "border-gold/50 bg-gold/[0.03]" : "border-white/10 bg-[#0A0A0A]"}`}>
            <div className="flex items-center gap-4">
              <div className={`p-3 border ${data?.has_live_pass ? "border-gold text-gold" : "border-white/20 text-white/40"}`}>
                <Radio size={20} />
              </div>
              <div>
                <p className="font-display text-lg font-bold uppercase" data-testid="account-live-status">
                  Live Pass {data?.has_live_pass ? "Active" : "— Not Active"}
                </p>
                <p className="text-white/50 text-sm">
                  {data?.has_live_pass
                    ? `Local Cable unlocked${data.live_until ? " · until " + fmtDate(data.live_until) : ""}`
                    : "Separate $7.99/mo channel access"}
                </p>
              </div>
            </div>
            {!data?.has_live_pass && (
              <button
                onClick={() => navigate("/#live")}
                data-testid="account-getpass-btn"
                className="bg-gold text-black font-semibold px-6 py-3 text-sm uppercase tracking-wider hover:opacity-90 active:scale-95 transition-all"
              >
                Get Live Pass
              </button>
            )}
          </div>
          )}

          {/* Billing history */}
          <div className="border border-white/10 bg-[#0A0A0A]">
            <div className="flex items-center gap-2 px-6 py-4 border-b border-white/10">
              <Receipt size={16} className="text-gold" />
              <p className="font-mono-imn text-xs tracking-[0.3em] uppercase text-white/60">Billing History</p>
            </div>
            {data?.history?.length ? (
              data.history.map((h, i) => (
                <div
                  key={i}
                  data-testid={`billing-row-${i}`}
                  className={`flex items-center justify-between px-6 py-4 ${i !== data.history.length - 1 ? "border-b border-white/10" : ""}`}
                >
                  <div>
                    <p className="text-sm capitalize">{h.label || `${h.plan || ""} plan`}</p>
                    <p className="font-mono-imn text-[11px] text-white/60">{fmtDate(h.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg">${(h.amount || 0).toFixed(2)}</p>
                    <p className={`font-mono-imn text-[10px] uppercase tracking-widest ${h.status === "paid" ? "text-gold" : "text-white/60"}`}>
                      {h.status}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="px-6 py-8 text-center text-white/40 text-sm">No transactions yet.</p>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
